import { api } from '../lib/api';

export interface Site {
  id: number;
  parentId: number | null;
  name: string;
  siteTypeItemId: number | null;
  isActive: number;
}

export interface BalanceRow {
  siteId: number;
  siteName: string | null;
  productId: number;
  productName: string;
  containerId: number | null;
  batchCode: string | null;
  uomItemId: number | null;
  uomLabel: string | null;
  kind: string | null;
  statusItemId: number | null;
  statusLabel: string | null;
  nectarItemId: number | null;
  nectarLabel: string | null;
  waterPercent: string | null;
  isActive: number | null;
  closedAt: string | null;
  qtyOnHand: string;
}

export interface ExplainLine {
  id: number;
  txId: number;
  txType: string;
  txTypeLabel: string;
  txDate: string;
  txNotes: string | null;
  refType: string | null;
  refId: number | null;
  siteId: number;
  siteName: string | null;
  containerId: number | null;
  qty: string;
  batchCode: string | null;
  uomLabel: string | null;
  createdByName: string | null;
  sourceTitle: string | null;
  sourcePath: string | null;
}

export interface LedgerTxDetailLine {
  id: number;
  siteId: number;
  siteName: string | null;
  productId: number;
  productName: string;
  containerId: number | null;
  containerCode: string | null;
  batchCode: string | null;
  qty: string;
  uomItemId: number | null;
  uomLabel: string | null;
  unitCostExVat: string | null;
}

export interface LedgerSourceField {
  label: string;
  value: string;
}

export interface LedgerSourceDetail {
  kind: string;
  title: string;
  subtitle: string;
  path: string | null;
  pathLabel: string | null;
  fields: LedgerSourceField[];
  rows: Array<Record<string, string | number | null>>;
}

export interface LedgerTxDetail {
  tx: {
    id: number;
    txType: string;
    txTypeLabel: string;
    txDate: string;
    siteId: number | null;
    siteName: string | null;
    refType: string | null;
    refId: number | null;
    notes: string | null;
    createdByName: string | null;
  };
  lines: LedgerTxDetailLine[];
  source: LedgerSourceDetail | null;
}

export const ledgerApi = {
  getSites: () => api.get<{ sites: Site[] }>('/sites'),
  getBalance: (params?: {
    siteId?: number;
    q?: string;
    categoryItemId?: number;
    statusItemId?: number;
    kind?: 'HONEY' | 'POLLEN' | '';
    onlyBatches?: boolean;
    isActive?: '1' | '0' | '';
  }) => {
    const search = new URLSearchParams();
    if (params?.siteId) {
      search.set('siteId', String(params.siteId));
    }
    if (params?.q && params.q.trim() !== '') {
      search.set('q', params.q.trim());
    }
    if (params?.categoryItemId) {
      search.set('categoryItemId', String(params.categoryItemId));
    }
    if (params?.statusItemId) {
      search.set('statusItemId', String(params.statusItemId));
    }
    if (params?.kind) {
      search.set('kind', params.kind);
    }
    if (params?.onlyBatches) {
      search.set('onlyBatches', '1');
    }
    if (params?.isActive) {
      search.set('isActive', params.isActive);
    }
    const suffix = search.size > 0 ? `?${search.toString()}` : '';
    return api.get<{ balance: BalanceRow[] }>(`/ledger/balance${suffix}`);
  },
  getExplain: (params: {
    siteId: number;
    productId: number;
    containerId?: number | null;
    batchCode?: string | null;
    uomItemId?: number | null;
    matchNoBatch?: boolean;
  }) => {
    const search = new URLSearchParams();
    search.set('siteId', String(params.siteId));
    search.set('productId', String(params.productId));
    if (params.containerId && params.containerId > 0) {
      search.set('containerId', String(params.containerId));
    } else if (params.matchNoBatch) {
      search.set('matchNoBatch', '1');
    } else if (params.batchCode !== undefined && params.batchCode !== null) {
      search.set('batchCode', params.batchCode);
    }
    if (params.uomItemId && params.uomItemId > 0) {
      search.set('uomItemId', String(params.uomItemId));
    }
    return api.get<{ lines: ExplainLine[] }>(`/ledger/explain?${search.toString()}`);
  },
  getTransactionDetail: (txId: number) =>
    api.get<LedgerTxDetail>(`/ledger/transactions/${txId}/detail`),
  createTransaction: (data: {
    txType: 'MOVE' | 'ADJUST' | 'DISPOSE';
    txDate?: string;
    siteId?: number | null;
    notes?: string;
    refType?: string;
    refId?: number | null;
    lines: Array<{
      productId: number;
      qty: number;
      uomItemId?: number | null;
      fromSiteId?: number | null;
      toSiteId?: number | null;
      siteId?: number | null;
      containerId?: number | null;
      batchCode?: string | null;
    }>;
  }) => api.post<{ id: number; txType: string }>('/ledger/transactions', data),
};
